import React, { useState, useEffect } from 'react'

import { Tooltip, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import { BASE_API } from 'configs/api'
import { useSnackbar } from 'notistack'
import { useDataProvider, useTranslate } from 'react-admin'
import { useForm } from 'react-final-form'
import { httpClient } from 'utils/httpClient'

import { lightergray2, orange } from '../../../constants/colorConstants'
import Exclamation from '../../../images/exclamation'
import Info from '../../../images/info'
import tracker from '../../../utils/tracker'
import { UploadDialog } from './uploadDialog'

// eslint-disable-next-line no-undef
const filesLimit = parseInt(process.env.UPLOAD_FILES_LIMIT ?? '10')

const directUploadFiles = (resource, props) => {
  const { files, timestamp, invoiceId } = props
  const formData = new FormData()
  formData.append('invoiceId', invoiceId)
  formData.append('timestamp', timestamp)
  files.map((file) => formData.append('file', file))

  return httpClient(`${BASE_API}/${resource}/files/upload`, {
    method: 'POST',
    body: formData,
    header: new Headers({
      ContentType: 'multipart/form-data',
      Accept: 'application/json',
    }),
  }).then(({ json }) => ({
    data: json,
  }))
}

export const InvoiceCreationUploadStep = ({
  resource,
  classes,
  invoiceId,
  savedFiles,
  setSaveFiles,
  state,
  setState,
  buildFileUploadState,
  changesCount,
  setChangedCount,
  timestamp,
}) => {
  const translate = useTranslate()
  const dataProvider = useDataProvider()
  const form = useForm()

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const [, setSnackbarKeys] = useState([])

  useEffect(() => {
    form.change('files', savedFiles)
  }, [form, savedFiles])

  const uploadFilesN = async (files, timestamp, invoiceId) => {
    if (!files || !files.length) return []
    return directUploadFiles(resource, {
      invoiceId,
      timestamp,
      files,
    })
  }

  const removeFile = async (fileId) => {
    if (!fileId) return []
    return dataProvider.removeFile(resource, {
      data: {
        fileId,
      },
    })
  }

  const handleDelete = async (fileToDelete) => {
    try {
      tracker.event({
        action: 'form_uploaded_file_removed',
        category: 'upload',
      })

      const newFiles = savedFiles.filter(
        (f) => f !== fileToDelete && f !== null
      )
      await removeFile(fileToDelete.fileId)
      setSaveFiles(newFiles)

      setState({
        ...buildFileUploadState(newFiles, false, false, false),
      })

      enqueueSnackbar(
        `${translate(
          'resources.invoices.actions.create.upload.uploadFileDialog.fileRemovedMessage'
        ).replace('{{fileName}}', fileToDelete.fileName)}`,
        { autoHideDuration: 3000, variant: 'info' }
      )
    } catch (e) {
      tracker.event({
        action: 'upload_error',
        category: 'upload',
      })
    }
  }

  const handleTooltipClosed = () => {
    tracker.event({
      action: 'invoice_advance_request_confirmation_dialog_info_hovered',
      category: 'invoiceAdvanceRequest',
    })
  }

  const handleOpen = () => {
    tracker.event({
      action: 'upload_files_dialog_opened',
      category: 'upload',
      label: 'UploadedFilesCount',
      value: savedFiles?.length,
    })
    setChangedCount(0)

    setState({
      ...buildFileUploadState(state.files, false, false, true),
    })
  }

  return (
    <>
      <span className={classes.requestedDocumentationRow}>
        <Exclamation color={orange} />
        <Typography className={classes.requestedDocumentation}>
          {translate(
            'resources.invoices.actions.create.upload.documentation.title'
          )}
        </Typography>
      </span>
      <div className={classes.documentsPanel}>
        <ul className={classes.documentList}>
          <li>
            <span className={classes.row}>
              <Typography className={classes.document}>
                {translate(
                  'resources.invoices.actions.create.upload.documentation.invoice'
                )}
              </Typography>
              <Typography className={classes.required}>
                {translate(
                  'resources.invoices.actions.create.upload.documentation.required'
                )}
              </Typography>
            </span>
          </li>
          <li>
            <span className={classes.row}>
              <Typography className={classes.document}>
                {translate(
                  'resources.invoices.actions.create.upload.documentation.proofOfDelivery'
                )}
              </Typography>
              <Typography className={classes.required}>
                {translate(
                  'resources.invoices.actions.create.upload.documentation.required'
                )}
              </Typography>
            </span>
          </li>
          <li>
            <span className={classes.row}>
              <Typography className={classes.document}>
                {translate(
                  'resources.invoices.actions.create.upload.documentation.purchaseOrder'
                )}
              </Typography>
              <Typography className={classes.required}>
                {translate(
                  'resources.invoices.actions.create.upload.documentation.required'
                )}
              </Typography>
            </span>
          </li>
          <li>
            <span className={classes.row315}>
              <Typography className={classes.document}>
                {translate(
                  'resources.invoices.actions.create.upload.documentation.proofOfAgreedDueDate'
                )}
              </Typography>
              <Tooltip
                className={classes.required}
                title={translate(
                  'resources.invoices.actions.create.upload.documentation.infoHoverText'
                )}
                onClose={handleTooltipClosed}
              >
                <Button className={classes.nohover}>
                  <Info color={lightergray2} />
                </Button>
              </Tooltip>
            </span>
          </li>
        </ul>
        <ul className={classes.documentList}>
          <li>
            <span className={classes.row}>
              <Typography className={classes.sea}>
                {translate(
                  'resources.invoices.actions.create.upload.documentation.sea'
                )}
              </Typography>
            </span>
          </li>
          <li>
            <span className={classes.row}>
              <Typography className={classes.document}>
                {translate(
                  'resources.invoices.actions.create.upload.documentation.billOfLading'
                )}
              </Typography>
            </span>
          </li>
          <li>
            <span className={classes.row}>
              <Typography className={classes.document}>
                {translate(
                  'resources.invoices.actions.create.upload.documentation.insurancePolicy'
                )}
              </Typography>
            </span>
          </li>
        </ul>
      </div>
      <div className={classes.uploadFilesPanel}>
        <Button
          variant="outlined"
          color="secondary"
          className={classes.buttonUpload}
          onClick={handleOpen}
          startIcon={<CloudUploadIcon />}
          disabled={savedFiles.length >= filesLimit}
        >
          {translate('resources.invoices.actions.create.upload.button')}
        </Button>
        <div className={classes.filesPanel}>
          {savedFiles?.length > 0
            ? savedFiles
                .filter((file) => file)
                .map((file) => (
                  <Chip
                    classes={{
                      root: classes.chip,
                    }}
                    key={file.url}
                    label={file.fileName}
                    onDelete={handleDelete.bind(this, file)}
                    color="secondary"
                    variant="outlined"
                  />
                ))
            : null}
        </div>

        <UploadDialog
          state={state}
          setState={setState}
          setSaveFiles={setSaveFiles}
          buildFileUploadState={buildFileUploadState}
          savedFiles={savedFiles}
          uploadFiles={uploadFilesN}
          changesCount={changesCount}
          setChangedCount={setChangedCount}
          invoiceId={invoiceId}
          enqueueSnackbar={enqueueSnackbar}
          closeSnackbar={closeSnackbar}
          setSnackbarKeys={setSnackbarKeys}
          filesLimit={filesLimit}
          timestamp={timestamp}
        />
      </div>
    </>
  )
}
